import { addLog } from 'assets/components/feedback/Feedback';
import ModalForm from 'assets/components/form/modalForm/ModalForm';
import ThemeModalFormDefault from 'assets/components/form/modalForm/themes/Theme.ModalForm.Default';
import Icon from 'assets/components/icon/Icon';
import MultilineTextInput from 'assets/components/inputs/multilineText/MultilineTextInput';
import NumberInput from 'assets/components/inputs/number/NumberInput';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import TextInput from 'assets/components/inputs/text/TextInput';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import useApi from 'assets/hooks/api/useApi';
import { getLocales } from 'assets/locales/Locale';
import { HttpStatus } from 'config/Api.Config';
import { cloneDeep, isEmpty, set } from 'lodash';
import claimTypeSaveApi from 'models/claims/claimType/save/Api.ClaimType.Save';
import claimTypeAutomationRuleDeleteApi from 'models/claims/claimTypeAutomationRule/delete/Api.ClaimTypeAutomationRule.Delete';
import claimTypeAutomationRuleSaveApi from 'models/claims/claimTypeAutomationRule/save/Api.ClaimTypeAutomationRule.Save';
import Product from 'models/productManagement/product/Model.Product';
import {
  hasIncompleteAutomationEffects,
  hasIncompleteAutomationExpressions,
} from 'module/automation/helpers/ruleHelpers';
import { createClaimAutomationSchema } from 'module/automation/schemas/ClaimAutomationSchema';
import { ClaimTypeForm, useClaimTypeProvider } from '../ClaimType.Hooks';
import ClaimTypeEditAutomationRules from './edit/automationRules/ClaimType.Edit.AutomationRules';
import ClaimTypeEditAggregates from './edit/ClaimType.Edit.Aggregates';
import ClaimTypeEditLossCodes from './edit/ClaimType.Edit.LossCodes';
import ClaimTypeEditReportSettings from './edit/ClaimType.Edit.ReportSettings';
import ClaimTypeEditDataPoints from './edit/dataPoints/ClaimType.Edit.DataPoints';
import ClaimTypeEditDocuments from './edit/documents/ClaimType.Edit.Documents';
import ClaimTypeEditJobItemTemplates from './edit/jobItemTemplates/ClaimType.Edit.JobItemTemplates';

export default function ClaimTypeEdit() {
  const { lang } = getLocales();
  const { selected, pageRouter, views, listApi, automationClaimStatuses, automationClaimJobStatuses } =
    useClaimTypeProvider();
  const { payload: productsPayload } = useApi({ action: Product.list });
  const automationSchema = createClaimAutomationSchema({
    claimStatuses: automationClaimStatuses,
    claimJobStatuses: automationClaimJobStatuses,
  });

  return (
    <>
      <ModalForm<ClaimTypeForm>
        subcomponents={ThemeModalFormDefault}
        dependencyArray={[selected, views.copy]}
        title={lang.claimType}
        default={{
          ...(views.copy ? { ...selected?.toJSON(), id: undefined, code: undefined } : selected),
          lossCodes: selected?.lossCodes?.map((it) => it.toJSON()) || [],

          // TODO: Not in use, use empty array for now.
          // claimTypeReportSettings: selected?.claimTypeReportSettings?.map((it) => it.toJSON()) || [],
          // claimTypeAggregates: selected?.claimTypeAggregates?.map((it) => it.toJSON()) || [],
          claimTypeAggregates: [],
          claimTypeReportSettings: [],

          claimTypeDataPoints: selected?.claimTypeDataPoints?.map((it) => it.toJSON()) || [],
          claimTypeJobItemTemplates: selected?.claimTypeJobItemTemplates?.map((it) => it.toJSON()) || [],
          claimTypeDocuments: selected?.claimTypeDocuments?.map((it) => it.toJSON()) || [],
          claimTypeAutomationRules: selected?.claimTypeAutomationRules?.map((it) => it.toJSON()),
        }}
        onCancel={pageRouter.updateParams}
        onSubmit={async ({ claimTypeAutomationRules, ...data }) => {
          const finalData = cloneDeep(data);
          // TODO: Not in use!
          // finalData.claimTypeAggregates?.forEach((it) => {
          //   delete it.id;
          //   delete it.version;
          // });
          // finalData.claimTypeReportSettings?.forEach((it) => {
          //   delete it.id;
          //   delete it.version;
          //   it.infoInputs = JSON.stringify(it.infoInputs) as any;
          //   it.reportRows = JSON.stringify(it.reportRows) as any;
          // });

          finalData.claimTypeDocuments?.forEach((it) => {
            delete it.id;
            delete it.version;
          });
          finalData.claimTypeDataPoints?.forEach((it) => {
            delete it.id;
            delete it.version;
          });
          finalData.claimTypeJobItemTemplates?.forEach((it) => {
            delete it.id;
            delete it.version;
          });

          finalData.products = finalData?.products?.map((it) => ({ id: it.id }));
          finalData.lossCodes = finalData?.lossCodes?.map((it) => ({ id: it.id }));

          const res = await claimTypeSaveApi({ ...finalData, claimTypeAutomationRules: [] });

          if (res.status !== HttpStatus.ok) addLog({ error: res.message });
          else {
            if (!isEmpty(claimTypeAutomationRules ?? []) && res.payload) {
              for (const claimTypeAutomationRule of claimTypeAutomationRules ?? []) {
                if (claimTypeAutomationRule.id > 0 && claimTypeAutomationRule.isDeleted) {
                  await claimTypeAutomationRuleDeleteApi(claimTypeAutomationRule.id);
                } else {
                  const automationRulePayload = {
                    ...claimTypeAutomationRule,
                    claimTypeId: res.payload,
                  };
                  if (!automationRulePayload.id || automationRulePayload.id <= 0) {
                    delete automationRulePayload.id;
                  }

                  await claimTypeAutomationRuleSaveApi(automationRulePayload);
                }
              }
            }
            listApi.execute((body) => body);
          }
        }}
        validation={(data, errors) => {
          if (!data.name) errors.name = lang.mustNotBeEmpty;
          if (!data.code) errors.code = lang.mustNotBeEmpty;
          // TODO: Not in use!
          // if (data.claimTypeReportSettings?.some((it) => !it.name)) {
          //   if (!errors.claimTypeReportSettings) errors.claimTypeReportSettings = {};
          //   errors.claimTypeReportSettings.name = lang.mustNotBeEmpty;
          // }
          if (!data.products) errors.products = { _objectError: lang.mustNotBeEmpty };
          if (
            data.claimTypeAutomationRules?.some(
              (rule) =>
                hasIncompleteAutomationExpressions(rule as Model.IClaimTypeAutomationRule) ||
                hasIncompleteAutomationEffects(rule as Model.IClaimTypeAutomationRule, automationSchema)
            )
          )
            set(errors, 'claimTypeAutomationRules._objectError', lang.required);
        }}
        tabs={[
          { id: 'info', icon: 'fas-info-circle', text: lang.info },
          // TODO: Not in use!
          // { id: 'reportSettings', icon: 'fas-file-contract', text: lang.report },
          // { id: 'aggregates', icon: 'fas-layer-group', text: lang.aggregate },
          { id: 'documents', icon: 'fas-file-alt', text: lang.documents },
          { id: 'lossCodes', icon: 'fas-list', text: lang.lossCode },
          { id: 'dataPoints', icon: 'fas-table', text: lang.dataPoints },
          { id: 'jobItemTemplates', icon: 'fas-list', text: lang.jobItemTemplates },
          { id: 'automationRules', icon: 'fas-list', text: lang.automationRules },
        ]}
        render={({ form, activeTab }) =>
          activeTab.id == 'info' ? (
            <FormWrapper>
              <TextInput
                name="name"
                label={lang.name}
                value={form.data.name}
                error={form.errors.name}
                onChange={form.update}
                icon={<Icon class="if-quill-pen" />}
              />
              <TextInput
                name="code"
                label={lang.code}
                value={form.data.code}
                error={form.errors.code}
                onChange={form.update}
                icon={<Icon class="if-quill-pen" />}
              />
              <MultilineTextInput
                name="description"
                label={lang.description}
                value={form.data.description}
                onChange={form.update}
                icon={<Icon class="if-quill-pen" />}
              />
              <SelectInput
                name="products"
                label={lang.products}
                data={productsPayload?.data?.map((item) => ({
                  ...item.displayInfo,
                  icon: <Icon class="fas-box" />,
                }))}
                isMultiselect
                error={form.errors?.products?._objectError}
                value={form.data.products?.map((it) => it.id)}
                onChange={({ products }: { products: number[] }) =>
                  form.update({
                    products: products
                      .map((it) => productsPayload?.data?.find((d) => d.id === it))
                      ?.filter((it) => (it ? true : false)),

                    // TODO: Not in use!
                    // claimTypeAggregates: products?.flatMap((selectedProductId, pIdx) => {
                    //   const matchedProduct = productsPayload.data?.find((p) => p.id === selectedProductId);
                    //   return (
                    //     matchedProduct?.insurers?.map((insurer, insurerIndex) => ({
                    //       id: -1 * (pIdx * 100 + insurerIndex),
                    //       version: 1,
                    //       totalAggregate: 0,
                    //       totalAggregatePriority: 0,
                    //       maxClaimAmount: 0,
                    //       maxClaimAmountPriority: 0,
                    //       maxNumberOfClaims: 0,
                    //       maxNumberOfClaimsPriority: 0,
                    //       houseAmount: 0,
                    //       insurerAmount: 0,
                    //       houseOpeningNotice: false,
                    //       insurerOpeningNotice: false,
                    //       allowOverride: false,
                    //       insurer: insurer.toJSON(),
                    //       insurerId: insurer.id,
                    //     })) || []
                    //   );
                    // }),
                  })
                }
                icon={<Icon class="if-box" />}
              />
              <NumberInput
                label={lang.legacyId}
                name="legacyClaimTypeId"
                value={form.data.legacyClaimTypeId ? Number(form.data.legacyClaimTypeId) : null}
                onChange={form.update}
                icon={<Icon class="fas-link" />}
              />
              {/* <CheckWrapper>
                <CheckInput
                  name="claimTypeJobItemsAutomationEnabled"
                  label={lang.isClaimTypeAutomationEnabled}
                  value={form.data.claimTypeJobItemsAutomationEnabled}
                  error={form.errors.claimTypeJobItemsAutomationEnabled}
                  onChange={form.update}
                  class={inputStyles.check.switch}
                  htmlElementProps={{
                    title: form.data.claimTypeJobItemsAutomationEnabled ? lang.disable : lang.enable,
                  }}
                />
              </CheckWrapper> */}
              &nbsp;
            </FormWrapper>
          ) : activeTab.id == 'reportSettings' ? (
            <ClaimTypeEditReportSettings />
          ) : activeTab.id == 'aggregates' ? (
            <ClaimTypeEditAggregates />
          ) : activeTab.id == 'documents' ? (
            <ClaimTypeEditDocuments />
          ) : activeTab.id == 'lossCodes' ? (
            <ClaimTypeEditLossCodes />
          ) : activeTab.id == 'dataPoints' ? (
            <ClaimTypeEditDataPoints />
          ) : activeTab.id == 'jobItemTemplates' ? (
            <ClaimTypeEditJobItemTemplates />
          ) : activeTab.id == 'automationRules' ? (
            <ClaimTypeEditAutomationRules />
          ) : null
        }
      />
    </>
  );
}
